/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { bindActionCreators } from "redux";
import { Search, RefreshCw, Plus, Inbox, X } from "lucide-react";
import RenderSupportPage from "../RenderSupportPage";
import Loader from "../../Components/Loader";
import TablePagination from "../../InventoryModel/components/TablePagination";
import { supportActionCreators } from "../../services/Actions";
import {
  TicketStatus, TicketPriority, TicketPrioritySelectable, TicketQueues, TicketCategories,
} from "../../contants";
import {
  StatusBadge, PriorityBadge, QueueBadge, timeAgo,
} from "../components/TicketBadges";

const PAGE_SIZE = 12;

// Sidebar/dashboard links arrive as ?view=…; each is just a preset over the
// same filter set so the queue stays one screen.
const VIEW_PRESETS = {
  escalated: { status: TicketStatus.ESCALATED },
  resolved: { status: TicketStatus.RESOLVED },
  open: { status: TicketStatus.OPEN },
  pending: { status: TicketStatus.PENDING },
};

function TicketQueue() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { GetTickets, CreateTicket, ResetSupportResponse } =
    bindActionCreators(supportActionCreators, dispatch);

  const support = useSelector((state) => state.support);
  const { tickets, response, isLoading } = support;

  const params = new URLSearchParams(location.search);
  const view = params.get("view");
  const queueParam = params.get("queue");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [queue, setQueue] = useState(queueParam || "ALL");
  const [priority, setPriority] = useState("ALL");
  const [page, setPage] = useState(1);
  const [openNew, setOpenNew] = useState(false);

  const [form, setForm] = useState({
    staff_id: "",
    phone: "",
    subject: "",
    queue: TicketQueues.GENERAL,
    category: "",
    priority: TicketPriority.NORMAL,
    description: "",
  });

  const preset = useMemo(() => VIEW_PRESETS[view] || {}, [view]);

  const fetchTickets = () =>
    GetTickets({ search, status, queue, priority, ...preset });

  // Re-query when the preset (URL) or any dropdown changes; free-text search is
  // submitted explicitly so it does not fire on every keystroke.
  useEffect(() => {
    fetchTickets();
    setPage(1);
  }, [view, status, queue, priority]);

  useEffect(() => {
    setQueue(queueParam || "ALL");
  }, [queueParam]);

  useEffect(() => {
    setPage(1);
  }, [tickets]);

  useEffect(() => {
    if (response?.state === "SUCCESS") {
      toast.success(response?.message);
      setOpenNew(false);
      setForm({
        staff_id: "", phone: "", subject: "", queue: TicketQueues.GENERAL,
        category: "", priority: TicketPriority.NORMAL, description: "",
      });
      setTimeout(() => {
        ResetSupportResponse();
        fetchTickets();
      }, 1200);
    } else if (response?.state === "ERROR") {
      toast.error(response?.message);
      setTimeout(() => ResetSupportResponse(), 1500);
    }
  }, [response]);

  const rows = tickets || [];
  const paged = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openTicket = (t) =>
    history.push(`/support/ticket-detailed?id=${t._id || t.ticket_no}`);

  const submitNew = (e) => {
    e.preventDefault();
    if (!form.staff_id || !form.subject || !form.description) {
      toast.error("Staff ID, subject and description are required");
      return;
    }
    CreateTicket(form);
  };

  const selectCls =
    "px-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500";

  const heading = view
    ? `${view.charAt(0).toUpperCase()}${view.slice(1)} Tickets`
    : "All Tickets";

  return (
    <RenderSupportPage title={heading}>
      {isLoading && <Loader />}
      <ToastContainer />

      {/* Filter bar */}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchTickets()}
              placeholder="Search ticket no / member / subject"
              className="pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg w-72 bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
            <option value="ALL">All statuses</option>
            {Object.values(TicketStatus).map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </select>

          <select value={queue} onChange={(e) => setQueue(e.target.value)} className={selectCls}>
            <option value="ALL">All queues</option>
            {Object.values(TicketQueues).map((q) => (
              <option key={q} value={q}>{q}</option>
            ))}
          </select>

          <select value={priority} onChange={(e) => setPriority(e.target.value)} className={selectCls}>
            <option value="ALL">All priorities</option>
            {Object.values(TicketPriority).map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>

          <button
            onClick={fetchTickets}
            className="p-2 rounded-lg bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-gray-600"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        <button
          onClick={() => setOpenNew(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
        >
          <Plus size={16} /> New Ticket
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
              <tr>
                {["Ticket", "Member", "Queue", "Priority", "Status", "Updated"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <Inbox size={30} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                    <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                      No tickets found
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                      Adjust the filters, or log a new ticket for a member.
                    </p>
                  </td>
                </tr>
              ) : (
                paged.map((t) => (
                  <tr
                    key={t._id || t.ticket_no}
                    onClick={() => openTicket(t)}
                    className="hover:bg-slate-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-bold text-slate-800 dark:text-slate-100 max-w-[280px] truncate">
                        {t.subject || "Untitled ticket"}
                      </p>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500">
                        #{t.ticket_no || "—"} · {t.category || "Uncategorised"}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                        {t.member?.name || t.member_name || "—"}
                      </p>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500">
                        {t.member?.staff_id || t.staff_id || "—"}
                      </p>
                    </td>
                    <td className="px-4 py-3"><QueueBadge queue={t.queue} /></td>
                    <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                    <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
                      {timeAgo(t.updated_at || t.updatedAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          page={page}
          pageSize={PAGE_SIZE}
          total={rows.length}
          onPageChange={setPage}
        />
      </div>

      {/* New ticket modal — for calls / walk-ins logged by an agent */}
      {openNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <form
            onSubmit={submitNew}
            className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-gray-700">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Log New Ticket</h3>
              <button type="button" onClick={() => setOpenNew(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">Member Staff ID</label>
                  <input
                    value={form.staff_id}
                    onChange={(e) => setForm({ ...form, staff_id: e.target.value })}
                    placeholder="e.g. 123456"
                    className={`${selectCls} w-full`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">Phone Number</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="e.g. 0244 123 456"
                    className={`${selectCls} w-full`}
                  />
                </div>
              </div>
              <p className="-mt-2 text-[11px] text-slate-400 dark:text-slate-500">
                The ticket number is texted to this number so the member can quote it when they call back. Leave blank to use the member's saved phone.
              </p>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">Subject</label>
                <input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Short summary of the issue"
                  className={`${selectCls} w-full`}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">Queue</label>
                  <select
                    value={form.queue}
                    onChange={(e) => setForm({ ...form, queue: e.target.value, category: "" })}
                    className={`${selectCls} w-full`}
                  >
                    {Object.values(TicketQueues).map((q) => (
                      <option key={q} value={q}>{q}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className={`${selectCls} w-full`}
                  >
                    {TicketPrioritySelectable.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className={`${selectCls} w-full`}
                >
                  <option value="">Select a category</option>
                  {(TicketCategories[form.queue] || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4}
                  placeholder="What did the member report?"
                  className={`${selectCls} w-full resize-none`}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setOpenNew(false)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
              >
                Create Ticket
              </button>
            </div>
          </form>
        </div>
      )}
    </RenderSupportPage>
  );
}

export default TicketQueue;
